import { create } from "zustand"
import { MOCK_SERVER_STORAGE_KEY } from "./const"
import type { MockRequestConfig, MockServerPersistedState, MockServerRequestDto } from "./types"

type MockServerState = MockServerPersistedState & {
  setApiKey: (apiKey: string) => void
  mergeRequests: (serverRequests: MockServerRequestDto[]) => void
  setRequestEnabled: (id: string, enabled: boolean) => void
  setResponseTag: (id: string, responseTag: string) => void
  setAllEnabled: (enabled: boolean) => void
}

const initialEmptyState: MockServerPersistedState = {
  apiKey: "",
  requests: {},
}

function readPersistedState(): MockServerPersistedState {
  try {
    const raw = localStorage.getItem(MOCK_SERVER_STORAGE_KEY)
    if (!raw) {
      return initialEmptyState
    }

    const parsed = JSON.parse(raw) as Partial<MockServerPersistedState>
    if (!parsed || typeof parsed !== "object") {
      return initialEmptyState
    }

    return {
      apiKey: typeof parsed.apiKey === "string" ? parsed.apiKey : "",
      requests: parsed.requests && typeof parsed.requests === "object" ? parsed.requests : {},
    }
  } catch {
    return initialEmptyState
  }
}

function persistState(state: MockServerPersistedState) {
  localStorage.setItem(
    MOCK_SERVER_STORAGE_KEY,
    JSON.stringify({
      apiKey: state.apiKey,
      requests: state.requests,
    } satisfies MockServerPersistedState)
  )
}

export const useMockServerStore = create<MockServerState>((set, get) => ({
  ...readPersistedState(),

  setApiKey: (apiKey) => {
    const next = { apiKey, requests: get().requests }
    persistState(next)
    set(next)
  },

  mergeRequests: (serverRequests) => {
    const current = get().requests
    const requests: Record<string, MockRequestConfig> = {}

    for (const serverRequest of serverRequests) {
      const existing = current[serverRequest.id]
      requests[serverRequest.id] = {
        id: serverRequest.id,
        method: serverRequest.method,
        path: serverRequest.path,
        enabled: existing?.enabled ?? false,
        responseTag: existing?.responseTag ?? "",
      }
    }

    const next = { apiKey: get().apiKey, requests }
    persistState(next)
    set(next)
  },

  setRequestEnabled: (id, enabled) => {
    const current = get().requests[id]
    if (!current) {
      return
    }

    const next = {
      apiKey: get().apiKey,
      requests: { ...get().requests, [id]: { ...current, enabled } },
    }
    persistState(next)
    set(next)
  },

  setResponseTag: (id, responseTag) => {
    const current = get().requests[id]
    if (!current) {
      return
    }

    const next = {
      apiKey: get().apiKey,
      requests: { ...get().requests, [id]: { ...current, responseTag } },
    }
    persistState(next)
    set(next)
  },

  setAllEnabled: (enabled) => {
    const requests = Object.fromEntries(
      Object.entries(get().requests).map(([id, request]) => [id, { ...request, enabled }])
    )
    const next = { apiKey: get().apiKey, requests }
    persistState(next)
    set(next)
  },
}))

export function hasEnabledMockRequests(state = useMockServerStore.getState()) {
  return Object.values(state.requests).some((request) => request.enabled)
}

export function getEnabledMockRequests() {
  return Object.values(useMockServerStore.getState().requests).filter((request) => request.enabled)
}
